import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import { CART_QUERY_KEYS } from '~/utils/cartCache';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { BUY_LIST_DETAIL_QUERY_KEY, BUY_LISTS_QUERY_KEY } from '~/api/buyList';
import { updateCartTransport } from '~/api/cartTransport';
import { quoteChangeQueryKeys } from '~/api/requestForQuote';

import type {
  PlatformProductCategory,
  UniqueProductFiltersInput,
} from '~/graphql/graphql';

// Releasing quote-awarded lines changes quote state but not the buy list index
const REMOVE_LINE_QUERY_KEYS = [
  ...CART_QUERY_KEYS,
  'shippingCost',
  ...quoteChangeQueryKeys().filter((key) => key !== BUY_LISTS_QUERY_KEY),
];

export const useGetAvailableUniqueProductsWithSellers = (
  page: number,
  size: number,
  filters?: UniqueProductFiltersInput,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const uniqueProductsQuery = graphql(`
    query AllUniqueProducts(
      $page: Int!
      $size: Int!
      $filters: UniqueProductFiltersInput
    ) {
      findAvailableUniqueProductsWithSellers(
        pageInput: { page: $page, size: $size }
        filters: $filters
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          lengths {
            actualLengthInches
            nominalLengthInches
            displayValue
            id
          }
          marketAvailability {
            listingsCount
            leadTimeMin
            leadTimeMax
            priceMin {
              amount
              currency
            }
            priceMax {
              amount
              currency
            }
            sellersCount
            totalStock {
              value
              unit
            }
          }
          landedPriceRange {
            min {
              amount
              currency
            }
            max {
              amount
              currency
            }
          }
          platformProduct {
            id
            name
            category
            finishCode
            standardUnitsCode
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
            platformProductVehicleCapacities {
              id
              transportVehicle {
                id
                name
                vehicleType
              }
              capacity {
                value
                unit
              }
              isOrganizationOverride
            }
            properties
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    uniqueProductsQuery,
    [
      'uniqueProductsWithSellers',
      page.toString(),
      size.toString(),
      JSON.stringify(filters ?? null),
    ],
    { page, size, filters },
    options
  );
};

export const useUpdateCart = () => {
  const addToCartMutation = graphql(`
    mutation updateCart(
      $quantity: QuantityInput
      $locationId: ID!
      $sellerOrganizationFacilityId: ID!
      $piecesPerBundle: Int
      $suggestedPrice: Float
      $platformProductInputFilter: PlatformProductInputFilter!
      $parentOrderId: ID
    ) {
      updateCart(
        quantity: $quantity
        locationId: $locationId
        sellerOrganizationFacilityId: $sellerOrganizationFacilityId
        piecesPerBundle: $piecesPerBundle
        suggestedPrice: $suggestedPrice
        platformProductInputFilter: $platformProductInputFilter
        parentOrderId: $parentOrderId
      ) {
        id
        items {
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          effectivePiecesPerBundle
          suggestedPrice {
            amount
            currency
          }
        }
      }
    }
  `);
  return useGraphqlMutation(addToCartMutation, [
    ...CART_QUERY_KEYS,
    'products',
    'shippingCost',
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useSetRandomTallyInCart = () => {
  const mutation = graphql(`
    mutation SetRandomTallyInCart(
      $sellerOrganizationFacilityId: ID!
      $locationId: ID!
      $platformProductId: ID!
      $bundles: [RandomTallyBundleInput!]!
      $parentOrderId: ID
    ) {
      setRandomTallyInCart(
        sellerOrganizationFacilityId: $sellerOrganizationFacilityId
        locationId: $locationId
        platformProductId: $platformProductId
        bundles: $bundles
        parentOrderId: $parentOrderId
      ) {
        id
      }
    }
  `);
  return useGraphqlMutation(mutation, [
    ...CART_QUERY_KEYS,
    'products',
    'shippingCost',
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useSetCartItemBuyCode = () => {
  const mutation = graphql(`
    mutation setCartItemBuyCode(
      $cartId: ID!
      $platformProductLengthId: ID!
      $buyCode: String
    ) {
      setCartItemBuyCode(
        cartId: $cartId
        platformProductLengthId: $platformProductLengthId
        buyCode: $buyCode
      ) {
        id
        items {
          buyCode
        }
      }
    }
  `);
  return useGraphqlMutation(mutation, []);
};

export const useRemoveCannedTallyItems = () => {
  const mutation = graphql(`
    mutation RemoveCannedTallyItems(
      $cartId: ID!
      $tallyPlatformProductId: ID!
    ) {
      removeCannedTallyItems(
        cartId: $cartId
        tallyPlatformProductId: $tallyPlatformProductId
      ) {
        id
      }
    }
  `);
  return useGraphqlMutation(mutation, REMOVE_LINE_QUERY_KEYS);
};

export const useRemoveCartItem = () => {
  const mutation = graphql(`
    mutation RemoveCartItem($cartId: ID!, $platformProductLengthId: ID!) {
      removeCartItem(
        cartId: $cartId
        platformProductLengthId: $platformProductLengthId
      ) {
        id
      }
    }
  `);
  return useGraphqlMutation(mutation, REMOVE_LINE_QUERY_KEYS);
};

export const useRemoveRandomTally = () => {
  const mutation = graphql(`
    mutation RemoveRandomTally($cartId: ID!, $tallyPlatformProductId: ID!) {
      removeRandomTally(
        cartId: $cartId
        tallyPlatformProductId: $tallyPlatformProductId
      ) {
        id
      }
    }
  `);
  return useGraphqlMutation(mutation, REMOVE_LINE_QUERY_KEYS);
};

export const useUpdateCartTransport = () => {
  const mutation = graphql(`
    mutation UpdateCartTransport(
      $cartId: ID!
      $incoTerm: LogisticsIncoterm!
      $transportVehicleId: ID
      $internationalShipping: InternationalShippingInput
    ) {
      updateCartTransport(
        cartId: $cartId
        incoTerm: $incoTerm
        transportVehicleId: $transportVehicleId
        internationalShipping: $internationalShipping
      ) {
        id
        transportVehicle {
          id
          name
        }
        deliveryMethod
        incotermLoading
        incotermDischarge
        incotermAddressDestination
        portOfLoading {
          id
          code
          name
        }
        portOfDischarge {
          id
          code
          name
        }
        shippingRoute {
          id
          status
          forwarder
          transitDays
        }
      }
    }
  `);
  return useGraphqlMutation(
    mutation,
    [...CART_QUERY_KEYS, 'shippingCost'],
    updateCartTransport
  );
};

export const useUpdateCartTarp = () => {
  const mutation = graphql(`
    mutation UpdateCartTarp($cartId: ID!, $tarp: Boolean!) {
      updateCartTarp(cartId: $cartId, tarp: $tarp) {
        id
        tarp
      }
    }
  `);
  return useGraphqlMutation(mutation, [...CART_QUERY_KEYS]);
};

export const useUpdateCartDeliveryAddress = () => {
  const mutation = graphql(`
    mutation UpdateCartDeliveryAddress(
      $cartId: ID!
      $deliveryAddress: DeliveryAddressInput
    ) {
      updateCartDeliveryAddress(
        cartId: $cartId
        deliveryAddress: $deliveryAddress
      ) {
        id
        deliveryAddress {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
          latitude
          longitude
        }
        incotermAddressDestination
      }
    }
  `);
  return useGraphqlMutation(mutation, [
    ...CART_QUERY_KEYS,
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useGetCart = (
  locationId?: string,
  options?: { enabled?: boolean; withContacts?: boolean }
) => {
  const withContacts = options?.withContacts ?? false;
  const query = graphql(`
    query GetCartsWithFilter($locationId: ID, $withContacts: Boolean!) {
      carts(filter: { locationId: $locationId }) {
        id
        sellerContacts @include(if: $withContacts) {
          id
          firstName
          lastName
          phoneNumber
          externalId
        }
        isFromQuote
        parentOrder {
          id
        }
        deliveryAddress {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
          latitude
          longitude
        }
        sellingOrganization {
          id
          name
          sellingOrganizationCode
        }
        items {
          platformProductsLengths {
            id
            isNominal
            latestPriceMid {
              amount
              currency
            }
            platformProduct {
              id
              name
              code
              category
              nominalThicknessInches
              nominalWidthInches
              actualThicknessInches
              actualWidthInches
              actualLengthInches
              standardUnitsCode
              piecesPerBundle {
                value
                unit
              }
              properties
            }
            platformProductVehicleCapacities {
              transportVehicle {
                id
                vehicleType
              }
              capacity {
                value
                unit
              }
              isOrganizationOverride
            }
            lengthDisplayValue
            length {
              id
              actualLengthInches
              nominalLengthInches
            }
          }
          price {
            amount
            currency
          }
          suggestedPrice {
            amount
            currency
          }
          effectivePrice {
            amount
            currency
          }
          quantityStored: quantity(mode: { mode: STORED }) {
            value
          }
          pricePerPiece {
            amount
            currency
          }
          suggestedPricePerPiece {
            amount
            currency
          }
          pickupPricePerPiece {
            amount
            currency
          }
          deliveryPricePerPiece {
            amount
            currency
          }
          quantity {
            value
            unit
          }
          effectivePiecesPerBundle
          tallyPlatformProduct {
            id
          }
          cannedTally {
            id
            name
          }
          buyCode
        }
        incoTerm
        deliveryMethod
        isInternational
        tarp
        currency
        incotermLoading
        incotermDischarge
        incotermAddressDestination
        portOfLoading {
          id
          code
          name
          city
          country
          type
        }
        portOfDischarge {
          id
          code
          name
          city
          country
          type
        }
        shippingRoute {
          id
          status
          forwarder
          hasForwarderContact
          transitDays
          freeDays
          freightCost {
            amount
            currency
          }
          incotermLoading
          incotermDischarge
        }
        transportVehicle {
          id
          name
          vehicleType
        }
        sellerOrganizationFacility {
          id
          transportVehicles {
            id
            name
            vehicleType
          }
          code
          nearestPort {
            id
            code
            name
            city
            country
            type
          }
          address {
            addressLine1
            addressLine2
            city
            country
            region
          }
        }
        shippingLocation {
          id
          name
          facility {
            transportVehicles {
              id
              name
              vehicleType
            }
            nearestPort {
              id
              code
              name
              city
              country
              type
            }
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              country
              latitude
              longitude
            }
          }
        }
      }
    }
  `);
  return useGraphqlQuery(
    query,
    ['carts', locationId ?? 'all', withContacts],
    { locationId, withContacts },
    { enabled: options?.enabled }
  );
};

export const useGetCartById = (
  cartId: string,
  options?: { enabled?: boolean }
) => {
  const query = graphql(`
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        recipients {
          id
        }
        lockedRecipientIds
        primaryContact {
          ...PrimaryContact
        }
        purchaseOrderDatesRequired
        deliveryAddress {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
          latitude
          longitude
        }
        sellingOrganization {
          id
          name
          sellingOrganizationCode
        }
        items {
          effectivePiecesPerBundle
          price {
            amount
            currency
          }
          suggestedPrice {
            amount
            currency
          }
          effectivePrice {
            amount
            currency
          }
          pickupPrice {
            amount
            currency
          }
          deliveryPrice {
            amount
            currency
          }
          quantityStored: quantity(mode: { mode: STORED }) {
            value
          }
          pricePerPiece {
            amount
            currency
          }
          suggestedPricePerPiece {
            amount
            currency
          }
          pickupPricePerPiece {
            amount
            currency
          }
          deliveryPricePerPiece {
            amount
            currency
          }
          quantity {
            value
            unit
          }
          platformProductsLengths {
            id
            isNominal
            platformProduct {
              id
              name
              category
              nominalThicknessInches
              nominalWidthInches
              actualThicknessInches
              actualWidthInches
              actualLengthInches
              standardUnitsCode
              properties
              piecesPerBundle {
                value
                unit
              }
            }
            platformProductVehicleCapacities {
              transportVehicle {
                id
                vehicleType
              }
              capacity {
                value
                unit
              }
            }
            lengthDisplayValue
            length {
              id
              actualLengthInches
              nominalLengthInches
            }
          }
          tallyPlatformProduct {
            id
          }
          cannedTally {
            id
            name
          }
          buyCode
        }
        incoTerm
        deliveryMethod
        isInternational
        tarp
        currency
        incotermLoading
        incotermDischarge
        incotermAddressDestination
        portOfLoading {
          id
          code
          name
          city
          country
          type
        }
        portOfDischarge {
          id
          code
          name
          city
          country
          type
        }
        shippingRoute {
          id
          status
          forwarder
          hasForwarderContact
          transitDays
          freeDays
          freightCost {
            amount
            currency
          }
          incotermLoading
          incotermDischarge
        }
        transportVehicle {
          id
          vehicleType
        }
        shippingLocation {
          id
          name
          facility {
            address {
              addressLine1
              city
              region
              zipCode
              latitude
              longitude
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['cart', cartId], { cartId }, options);
};

export const useGetProductCategories = () => {
  return useGraphqlQuery(
    graphql(`
      query ProductCategories {
        platformProductCategories {
          value
          label
        }
      }
    `),
    ['productCategories']
  );
};

export const useGetProductFilterOptions = (
  category?: PlatformProductCategory
) => {
  return useGraphqlQuery(
    graphql(`
      query ProductFilterOptions($category: PlatformProductCategory!) {
        platformProductDimensions {
          nominalThicknessInches
          nominalWidthInches
        }
        productPropertyKeys(category: $category) {
          key
          displayName
          isArray
        }
      }
    `),
    ['productFilterOptions', category ?? 'All Categories'],
    { category: category as PlatformProductCategory },
    { enabled: !!category }
  );
};

export const useGetBuyerInventoryLocations = () => {
  return useGraphqlQuery(
    graphql(`
      query BuyerInventoryLocations {
        allUniqueProductInventoryLocationsForBuyer {
          id
          name
          facility {
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              country
            }
          }
        }
      }
    `),
    ['buyerInventoryLocations']
  );
};
